import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit, Activity, Network, Wrench, RefreshCw, Router, Cloud, Cpu, Terminal, AlertTriangle, Shield, Server, BarChart3, Route } from 'lucide-react';
import StatusBadge from '../components/common/StatusBadge';
import Tabs, { type Tab } from '../components/common/Tabs';
import EdgeModal from '../components/edges/EdgeModal';
import SyncConfigButton from '../components/edges/SyncConfigButton';
import HealthOverview from './edges/health/HealthOverview';
import ConfigurationSummary from './edges/monitoring/ConfigurationSummary';
import VPPStatus from './edges/monitoring/VPPStatus';
import DiagnosticTools from './monitoring/DiagnosticTools';
import SSHAccessSummary from '../components/edges/SSHAccessSummary';
import AlertsDashboard from '../components/edges/AlertsDashboard';
import ACLOverview from './edges/acl/ACLOverview';
import RoutingPoliciesSummary from './edges/routing/RoutingPoliciesSummary';
import FlowExportSummary from './edges/monitoring/FlowExportSummary';
import { edgeService, observabilityService } from '../services';
import type { Edge } from '../services/edge.service';
import { useToast } from '../components/common/ToastContainer';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { USER_TYPES } from '../utils/constants';

/**
 * Determine if an edge is online based on VictoriaMetrics telemetry data.
 * An edge is online if it has a CPU metric — this means VictoriaMetrics
 * received fresh observability data from the edge (published every 15s).
 */
const isEdgeOnline = (
  telemetry: { cpu?: number } | undefined | null,
): boolean => {
  return !!telemetry && telemetry.cpu !== undefined;
};

const EdgeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || undefined;
  const toast = useToast();
  const { canUpdate } = usePermissions();
  // Super-Admin-only gate for the diagnostic/SSH surfaces on this page:
  //   - VSNP-964: the SSH tunnel status summary tab (if you can't SSH, tunnel
  //     state is component-level detail).
  //   - VSNP-960: the VPP Status tab (raw VPP interfaces/routes/plugins/services
  //     a Tenant Admin has no need to see and cannot act on).
  // Concrete user-type check — NOT usePermissions()/isSuperAdmin, which collapse
  // Channel Admin (type 5) into Super Admin. Channel Admin is excluded here,
  // matching the API guard's fail-closed decision.
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [telemetry, setTelemetry] = useState<any>(null);
  const [configSummaryRefresh, setConfigSummaryRefresh] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchEdge = async () => {
      try {
        const edgeData = await edgeService.getById(id);

        // Automatic redirection for legacy Snowflake ID URLs (Phase 3)
        const isSnowflakeId = /^-?\d+$/.test(id);
        if (isSnowflakeId && edgeData.devSerNum) {
          navigate(`/edges/${edgeData.devSerNum}`, { replace: true });
          return;
        }

        setEdge(edgeData);
      } catch (err: any) {
        setError(err.message || 'Failed to load edge details');
      } finally {
        setLoading(false);
      }
    };

    fetchEdge();
  }, [id, navigate]);

  useEffect(() => {
    if (edge?.devSerNum) {
      const guid = edge.devSerNum;

      const fetchTelemetry = async (isBackground = false) => {
        try {
          const options = isBackground ? { showLoading: false } : undefined;
          const res = await observabilityService.getEdgesTelemetry(options);
          if (res.status === 'success' && res.data[guid.toLowerCase()]) {
            setTelemetry(res.data[guid.toLowerCase()]);
          } else {
            // No telemetry record for this edge — represent that as a
            // sentinel empty object so callers can distinguish "fetched,
            // no signal" (= offline) from "haven't fetched yet" (= null).
            // Without this, every page load would briefly render the
            // offline banner while the first fetch was in flight.
            setTelemetry((prev: any) => prev ?? {});
          }
        } catch (err) {
          console.error('Failed to fetch telemetry for detail:', err);
          setTelemetry((prev: any) => prev ?? {});
        }
      };

      fetchTelemetry();
      const interval = setInterval(() => fetchTelemetry(true), 30000);
      return () => clearInterval(interval);
    }
  }, [edge?.devSerNum]);

  const handleSave = async (updatedEdge: any) => {
    if (!id) return;
    try {
      await edgeService.update(id, {
        devName: updatedEdge.name,
        devType: updatedEdge.type || 'Edge',
        devLocation: updatedEdge.location || '',
      });
      const edgeData = await edgeService.getById(id);
      setEdge(edgeData);
      setIsEditModalOpen(false);
      toast.success('Edge Updated', 'Edge information has been updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', err.message || 'Failed to update edge');
    }
  };

  // Helper to get edge type icon
  const getEdgeTypeIcon = (devType?: string | number) => {
    const label = getEdgeTypeLabel(devType);
    if (label === 'Cloud Edge') return Cloud;
    if (label === 'NaaS Edge') return Cpu;
    return Router;
  };

  // Helper to get edge type label
  const getEdgeTypeLabel = (devType?: string | number) => {
    if (!devType) return 'Edge';
    const typeStr = String(devType).toLowerCase();
    if (typeStr.includes('cloud')) return 'Cloud Edge';
    if (typeStr.includes('naas')) return 'NaaS Edge';
    // Handle legacy numeric values
    const num = Number(devType);
    if (num === 2) return 'Cloud Edge';
    if (num === 3) return 'NaaS Edge';
    return 'Edge';
  };


  // Convert API Edge to EdgeModal format
  const edgeForModal = edge ? {
    id: edge.id,
    name: edge.devName,
    type: getEdgeTypeLabel((edge as any).devType),
    location: edge.devLocation || '',
    model: (edge as any).edgeModel || edge.devModel || '-',
    serialNumber: edge.devSerNum,
    ipAddress: edge.publicIp || edge.ip || '',
    version: edge.devVersion || '',
    uptime: '',
    cpu: '',
    memory: '',
    lastSeen: '',
    status: (edge.upInformation ? 'Online' : 'Offline') as 'Online' | 'Offline',
  } : undefined;

  // Stable callback to prevent infinite re-render loop (React error #185)
  // Must be before early returns to comply with React hooks rules
  const handleRefreshReady = useCallback((fn: () => void) => {
    setConfigSummaryRefresh(() => fn);
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading edge details...</div>;
  }

  if (error || !edge) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Edge not found'}</div>
      </div>
    );
  }



  const tabs: Tab[] = [
    { id: 'overview', label: 'Health Overview', icon: Activity, content: <HealthOverview key="overview" edgeId={edge.devSerNum} /> },
    { id: 'summary', label: 'Configuration Summary', icon: Network, content: <ConfigurationSummary key="summary" edgeId={edge.devSerNum} onRefreshReady={handleRefreshReady} isOffline={telemetry !== null && !isEdgeOnline(telemetry)} isOnline={isEdgeOnline(telemetry)} /> },
    // VSNP-960: VPP Status is a Super-Admin-only diagnostic tab.
    ...(isSuperAdmin ? [
      { id: 'vpp-status', label: 'VPP Status', icon: Server, content: <VPPStatus key="vpp-status" edgeId={edge.devSerNum} isOnline={isEdgeOnline(telemetry)} /> },
    ] : []),
    { id: 'acl', label: 'ACL', icon: Shield, content: <ACLOverview key="acl" edgeGuid={edge.devSerNum} edgeName={edge.devName} readOnly={true} /> },
    { id: 'routing-policies', label: 'Routing Policies', icon: Route, content: <RoutingPoliciesSummary key="routing-policies" edgeId={edge.devSerNum} tenantId={edge.tenantId} /> },
    { id: 'flow-export', label: 'Flow Export', icon: BarChart3, content: <FlowExportSummary key="flow-export" edgeId={edge.devSerNum} /> },
    // VSNP-964: SSH tunnel status summary is Super-Admin-only.
    ...(isSuperAdmin ? [
      { id: 'ssh-access', label: 'SSH Access', icon: Terminal, content: <SSHAccessSummary key="ssh-access" edgeGuid={edge.devSerNum} /> },
    ] : []),
    { id: 'alerts', label: 'Alerts', icon: AlertTriangle, content: <AlertsDashboard key="alerts" edgeGuid={edge.devSerNum} /> },
    { id: 'diagnostics', label: 'Diagnostics', icon: Wrench, content: <DiagnosticTools key="diagnostics" edgeId={edge.devSerNum} edgeOnline={isEdgeOnline(telemetry)} /> },
  ];

  // VSNP-964 / VSNP-960 fallback: a deep link to a tab that was filtered out
  // for this user (?tab=ssh-access or ?tab=vpp-status) must not render a blank
  // pane. Tabs falls back to tabs[0] when defaultTab is undefined, so drop an
  // unknown/filtered tab param.
  const safeTabParam = tabs.some((t) => t.id === tabParam) ? tabParam : undefined;

  const edgeType = getEdgeTypeLabel((edge as any).devType);
  const EdgeIcon = getEdgeTypeIcon((edge as any).devType);
  const online = isEdgeOnline(telemetry);
  const model = (edge as any).edgeModel || edge.devModel || '-';
  const firmware = (edge as any).firmwareVersion || edge.devVersion || '-';
  const agentVersion = (edge as any).agentVersion || '-';
  const vppMode = (edge as any).vppMode || null;
  const dateSettings = useSettingsStore.getState().getDateFormatSettings();

  return (
    <div className="space-y-6">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/edges')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <EdgeIcon className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{edge.devName}</h1>
            <span className="text-sm text-gray-500">{edge.devSerNum}</span>
          </div>
          <StatusBadge status={online ? 'Online' : 'Offline'} />
          {vppMode && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${vppMode === 'AF_PACKET' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
              }`}>
              {vppMode}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {configSummaryRefresh && (
            <button
              onClick={configSummaryRefresh}
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-md font-medium text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              title="Refresh Configuration Summary data"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          )}
          <SyncConfigButton edgeId={edge.devSerNum} />
          {canUpdate && (
            <button onClick={() => navigate(`/edges/${id}/config`)} className="btn-secondary inline-flex items-center space-x-2 text-sm">
              <Edit className="w-4 h-4" />
              <span>Configure</span>
            </button>
          )}
          {canUpdate && (
            <button onClick={() => setIsEditModalOpen(true)} className="btn-primary inline-flex items-center space-x-2 border border-transparent text-sm">
              <Edit className="w-4 h-4" />
              <span>Edit Info</span>
            </button>
          )}
        </div>
      </div>

      {/* Edge Details Card */}
      <div className="card">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</span>
            <p className="mt-1 text-sm font-medium text-gray-900">{edgeType}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Model</span>
            <p className="mt-1 text-sm font-medium text-gray-900">{model}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</span>
            <p className="mt-1 text-sm font-medium text-gray-900 font-mono">{edge.publicIp || edge.ip || '-'}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Location</span>
            <p className="mt-1 text-sm font-medium text-gray-900">{edge.devLocation || '-'}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Firmware</span>
            <p className="mt-1 text-sm font-medium text-gray-900">{firmware}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Version</span>
            <p className="mt-1 text-sm font-medium text-gray-900 font-mono">{agentVersion}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Provisioned</span>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {(edge as any).createTime ? formatDate((edge as any).createTime, dateSettings) : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} defaultTab={safeTabParam} />
      </div>

      {/* Edit Modal */}
      {edgeForModal && (
        <EdgeModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSave}
          edge={edgeForModal}
          mode="edit"
        />
      )}

    </div>
  );
};

export default EdgeDetail;
